import { Modal, ModalProps } from 'antd';
import { css } from '@emotion/css';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslate } from '@tolgee/react';
import ProxyVideo from 'components/ProxyVideo';
import { shopeeVideoBackupService } from 'services/domainBackup';

interface VideoModalProps extends ModalProps {
  url: string;
  localVideoUrl?: string;
  controls: boolean;
  channel?: Conversation.Message['channel'];
}

export const VideoModal = (props: VideoModalProps) => {
  const { url: originalUrl, localVideoUrl, channel, visible, controls, ...rest } = props;
  const { t } = useTranslate();
  const [failed, setFailed] = useState(false);

  const [url, setURL] = useState(originalUrl);
  // 超时定时器引用
  const timeoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  // 是否已经开始播放
  const hasStartedPlayingRef = useRef(false);

  useEffect(() => {
    if (visible) {
      if (originalUrl) {
        setURL(originalUrl);
        // 重置状态
        hasStartedPlayingRef.current = false;
        setFailed(false);
        // 清除之前的超时定时器
        if (timeoutTimerRef.current) {
          clearTimeout(timeoutTimerRef.current);
          timeoutTimerRef.current = null;
        }
      } else if (localVideoUrl) {
        setURL(localVideoUrl);
      }
    }
  }, [visible, originalUrl, localVideoUrl]);

  /**
   * 设置超时检测
   */
  const setupTimeoutCheck = useCallback(
    (videoUrl: string) => {
      if (channel !== 'SHOPEE' || !videoUrl) {
        return;
      }

      // 清除之前的定时器
      if (timeoutTimerRef.current) {
        clearTimeout(timeoutTimerRef.current);
      }

      // 10 秒超时检测
      timeoutTimerRef.current = setTimeout(async () => {
        // 如果还没有开始播放，尝试切换域名
        if (!hasStartedPlayingRef.current && !failed) {
          // 标记当前域名为不可用（因为加载超时）
          shopeeVideoBackupService.markDomainAsUnavailable(videoUrl);
          // 通过赛马机制寻找最快可访问的备份域名
          const newUrl = await shopeeVideoBackupService.findFastestBackupDomain(videoUrl);
          if (!newUrl && localVideoUrl) {
            // 如果所有备份域名都尝试过了，使用本地视频
            setURL(localVideoUrl);
          } else if (!newUrl && !localVideoUrl) {
            setFailed(true);
          } else if (newUrl) {
            // 如果切换成功，重新设置超时检测
            setURL(newUrl);
            setupTimeoutCheck(newUrl);
          }
        }
      }, 10000);
    },
    [channel, localVideoUrl, failed],
  );

  useEffect(() => {
    if (visible && url && channel === 'SHOPEE') {
      setupTimeoutCheck(url);
    }

    return () => {
      if (timeoutTimerRef.current) {
        clearTimeout(timeoutTimerRef.current);
        timeoutTimerRef.current = null;
      }
    };
  }, [visible, url, channel, setupTimeoutCheck]);

  return (
    <Modal
      {...rest}
      visible={visible}
      title={null}
      destroyOnClose
      footer={null}
      wrapClassName={modalWrapperClassName}
    >
      <ProxyVideo
        controls={controls}
        autoPlay
        src={url}
        onPlaying={() => {
          hasStartedPlayingRef.current = true;
          // 清除超时定时器
          if (timeoutTimerRef.current) {
            clearTimeout(timeoutTimerRef.current);
            timeoutTimerRef.current = null;
          }
          if (failed) {
            setFailed(false);
          }
        }}
        onError={async () => {
          if (url) {
            // 清除超时定时器
            if (timeoutTimerRef.current) {
              clearTimeout(timeoutTimerRef.current);
              timeoutTimerRef.current = null;
            }

            // SHOPEE 视频灾备切换逻辑
            if (channel === 'SHOPEE') {
              // 标记当前域名为不可用
              shopeeVideoBackupService.markDomainAsUnavailable(url);
              // 通过赛马机制寻找最快可访问的备份域名
              const newUrl = await shopeeVideoBackupService.findFastestBackupDomain(url);
              if (newUrl) {
                // 切换成功，重新设置超时检测
                setURL(newUrl);
                setupTimeoutCheck(newUrl);
                return;
              }
            }

            // 如果所有备份域名都尝试过了，使用本地视频
            if (localVideoUrl) {
              setURL(localVideoUrl);
              return;
            }

            // 最终失败
            setFailed(true);
          }
        }}
      >
        <track kind="captions" />
      </ProxyVideo>
      {failed && (
        <p>
          {t('ViewsChat.视频播放失败')}
          <br />
          {t('ViewsChat.请下载后使用本地播放器播放')}
        </p>
      )}
    </Modal>
  );
};

export const modalWrapperClassName = css`
  > .ant-modal {
    top: 0;
    width: 100vw !important;
    max-width: 100vw;
    height: 100vh;
    padding: 0;
  }
  .ant-modal-content,
  .ant-modal-body {
    position: relative;
    width: 100%;
    height: 100%;
    background: transparent;
    p {
      position: absolute;
      top: 50%;
      width: 100%;
      text-align: center;
      color: #fff;
    }
  }
  .ant-modal-close-x {
    color: #fff;
  }
  video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;
